import { AdhesionUnitWatcherInterface } from '@mbrtargeting/metatag-shared-types';
import { addCssToElement, BehaviorSubject, createDOMRectObservable, createElementIntersectionObservable, createMutationObservable, getElementPosition, getScrollPosition, getViewportDimensions, isSelectorValid } from '@mbrtargeting/metatag-utils';

type EffectCleanupFunction = () => void;
type EffectFunction = (options: StickyNodeOptions) => EffectCleanupFunction

//StickyNode will switch between these 3 states, corresponding to a CSS class
enum States {
    FIXED = 'sdgFixed',
    STATIC = 'sdgStatic',
    STOPPED = 'sdgStopped'
}

const StateList: States[] = [States.FIXED, States.STATIC, States.STOPPED];

//Only when sdgStickyNode is applied to an element, should our state classes affect the node.
const stickyNodeClass = `
    .sdgStickyNode {
        min-width: var(--sticky-min-width, 0) !important;
     }
    .sdgStickyNode.${States.FIXED} {
        position: fixed !important;
        top: var(--sticky-adhesion-height, 0) !important;
        transform: var(--sticky-node-translate, unset) !important;
    }
    .sdgStickyNode.${States.STOPPED} {
        position: absolute !important;
        top: calc(var(--sticky-stop-top, 0) - var(--sticky-node-height, 0) - var(--sticky-parent-top, 0)) !important;
        transform: var(--sticky-node-translate, unset) !important;
    }
    .sdgStickyNode.${States.STATIC} {
        position: relative !important;
    }`;
addCssToElement(stickyNodeClass, document.head);

/**
 * StickyStopElement might not be present on site right away, observe changes to DOM and report when element is found.
 */
const createStickyStopObserver = (stickyStopUnitQuery: string, signal: AbortSignal) => {
    const getStickyStopElement = (): Element | null => {
        return isSelectorValid(stickyStopUnitQuery) ? document.querySelector(stickyStopUnitQuery) : null;
    };

    const searchStickyStop$ = new BehaviorSubject<Element | null>(null);
    const observer = createMutationObservable(document, { childList: true, subtree: true });
    observer.subscribe(() => {
        const result = getStickyStopElement();

        //observable will only expose changes
        if (searchStickyStop$.value === result) return;

        searchStickyStop$.next(result);
    }, { signal });

    return searchStickyStop$;
};

export interface StickyNodeOptions {
    targetNode: HTMLElement;
    stickyStopUnitQuery: string;
    stickyStop: boolean;
    adhesionUnitWatcher: AdhesionUnitWatcherInterface;
    stabilizedWidth: number;
}

/**
 * Will make given targetNode "sticky", scrolling along with the user.
 * Sticky will start when user scrolls the site down and either adhesionUnit or upper browser border will start touching targetNode.
 * Sticky should stop if a "stickyStop" element is present in config, just before the stickyElement will touch it.
 */
export const STICKY_NODE: EffectFunction = (options: StickyNodeOptions) => {
    const { targetNode, adhesionUnitWatcher, stickyStopUnitQuery, stabilizedWidth = 0 } = options;
    const ac: AbortController = new AbortController;
    const { signal } = ac;

    const styleNode = (targetNode.getRootNode() instanceof ShadowRoot) ? addCssToElement(stickyNodeClass, targetNode) : undefined;

    //height of stickyNode will affect positioning when avoiding stickyStop
    createDOMRectObservable(targetNode, { events: ['resize'] }).subscribe(({ height }) => {
        targetNode.style.setProperty('--sticky-node-height', height + 'px');
    }, { signal });

    //parent node absolute top value will affect start point of sticky effect
    const parentTop$ = new BehaviorSubject<number>(0);

    //parent node left value will affect if we need to translate adslot to the left
    const parentLeft$ = new BehaviorSubject<number>(0);

    createDOMRectObservable(targetNode.parentElement!, { events: ['resize', 'scroll'] }).subscribe(({ top, left }) => {
        const { scrollTop, scrollLeft } = getScrollPosition(targetNode.ownerDocument?.defaultView || window);
        const absoluteTop = Math.round(top) + scrollTop;
        const absoluteLeft = Math.round(left) + scrollLeft;

        if (parentLeft$.value !== absoluteLeft) parentLeft$.next(absoluteLeft);
        if (parentTop$.value !== absoluteTop) parentTop$.next(absoluteTop);
    }, { signal });

    parentTop$.subscribe(top => targetNode.style.setProperty('--sticky-parent-top', top + 'px'), { signal });
    parentLeft$.subscribe(left => {
        if (stabilizedWidth) return; //no need to translate if width is set

        //ad will slide into content when positioned on left part of site AND no width is set, fix by translating whole node to left
        targetNode.style.setProperty('--sticky-node-translate', (getViewportDimensions().viewportWidth / 2 > left) ? 'translate(-100%, 0)' : 'unset');
    });

    //applies given state/css class and removes all others. Will collect changes into a single frame calculation.
    const applyStateToNode = (targetState: States) => {
        const classes = targetNode.classList;
        classes.add(targetState);
        classes.remove(...StateList.filter(entry => entry !== targetState));
    };

    /**
     * Handle stickyStop, element at the bottom of the page which stickyNode should not overlap / collide with.
     * Wait for stickyStop element to be found and observe targetNode for intersections between those two.
     *
     * If both elements will collide, stop scroll-along of targetNode just before collision. When the user scrolls up again, restart sticky.
     */
    const stickyStopElement$ = createStickyStopObserver(stickyStopUnitQuery, signal);
    stickyStopElement$
        .switchMap((stickyStopElement) => createElementIntersectionObservable({
            element1: targetNode,
            modifier1: {
                top: 0,
                left: 0,
                right: 0,
                bottom: 1, //report intersection 1 pixel before elements would intersect visually
            },
            element2: stickyStopElement!,
            modifier2: {
                top: 0,
                left: 0,
                right: 0,
                bottom: 5000, //inflate stickyStop height, so scrolling further than stickyStop does not restart sticky below it
            }
        }))
        //when fixed and intersecting, stop stickyNode before visually colliding with stickyStopElement
        .subscribe(intersection => {
            if (!intersection || !targetNode.classList.contains('sdgFixed')) return;

            const { top } = getElementPosition(stickyStopElement$.value!);

            targetNode.style.setProperty('--sticky-stop-top', top + 'px');
            window.requestAnimationFrame(() => applyStateToNode(States.STOPPED));
        }, { signal });

    /**
     * Handle adhesionUnit (website sticky navigation bar at the top of page).
     * Ads should not collide with it, so try to determine the height of the element and update it constantly.
     * When height changes, inform all related listeners.
     */
    const adhesionUnitHeight$ = new BehaviorSubject(0);

    //update height of adhesionUnit only if targetNode intersects, otherwise don't care
    adhesionUnitWatcher.observe(targetNode, ({ isIntersecting, height }) => {
        if (isIntersecting && adhesionUnitHeight$.value !== height) adhesionUnitHeight$.next(Math.floor(height));
    });

    //tear down for adhesionUnitWatcher todo add options argument for observer to pass on signal directly.
    signal.addEventListener('abort', () => {
        adhesionUnitWatcher.unobserve(targetNode);
    });

    adhesionUnitHeight$.subscribe(height => {
        //when stickyNode is currently fixed, use changed height to position stickyNode away from adhesionUnit
        targetNode.style.setProperty('--sticky-adhesion-height', height + 'px');
    }, { signal });

    /**
     * Handle user scrolling:
     * When user scrolls down and over targetNode's start position, make node sticky.
     * As soon as user scrolls back to top, remove sticky effect when start position is reached again.
     *
     * Should targetNode be static after reaching stickyStopElement, check if this is still the case, or we can restart scroll-along
     */
    const scrollHandler = () => {
        window.requestAnimationFrame(() => {
            const adhesionUnitHeight = adhesionUnitHeight$.value;

            // Sticky should start when user scrolls down past targetNodes start position and end when user scrolls back to top.
            const allowSwitchToFixedState = getScrollPosition().scrollTop > Math.max(0, parentTop$.value - adhesionUnitHeight);

            if (targetNode.classList.contains('sdgStopped')) {
                //only invest resources if we really need to check
                if (targetNode.getBoundingClientRect().top > adhesionUnitHeight) {
                    applyStateToNode(States.FIXED);
                }
            }

            if (targetNode.classList.contains('sdgStatic') && allowSwitchToFixedState) {
                applyStateToNode(States.FIXED);
            } else if (targetNode.classList.contains('sdgFixed') && !allowSwitchToFixedState) {
                applyStateToNode(States.STATIC);
            }
        });
    };
    window.addEventListener('scroll', scrollHandler, { signal });

    //ad will slightly jump, if ad is smaller than width set by publisher on parentNode. Fixed by setting min-width
    if (stabilizedWidth > 0) targetNode.style.setProperty('--sticky-min-width', stabilizedWidth + 'px');

    //start the effect
    targetNode.classList.add('sdgStatic', 'sdgStickyNode');
    scrollHandler(); //initial evaluation

    return () => {
        ac.abort();
        targetNode.classList.remove('sdgStickyNode', ...StateList);
        styleNode?.remove();
    };
};
